/* Created by Interactive 63 - https://interactive63.com */
/**
 * The pure rules for which element a stage click selects, kept free of the DOM so they can be
 * unit tested. Selection depth works like a design tool: clicks pick top-level groups first and
 * double clicks drill inwards.
 */
import type { ElementId, ElementMeta } from '../../../core/model/types';

export type ClickKind = 'click' | 'drill' | 'deep';

/** Returns the ancestors of an element and the element itself, outermost first. */
export function chainFor(elements: Record<ElementId, ElementMeta>, id: ElementId): ElementId[] {
  const chain: ElementId[] = [];
  const seen = new Set<ElementId>();
  for (let current: ElementId | null = id; current && !seen.has(current);) {
    seen.add(current);
    const element: ElementMeta | undefined = elements[current];
    if (!element) break;
    chain.unshift(current);
    current = element.parentId;
  }
  return chain;
}

/**
 * Chooses the element a click on `hitId` should select.
 *
 * - `click`: the top-level ancestor, or, when something inside that ancestor is already selected,
 *   the element at the same depth as that selection which contains the hit.
 * - `drill` (double click): one level deeper than the current selection in that tree.
 * - `deep` (Ctrl + click): the hit element itself.
 */
export function resolveSelectionTarget(
  elements: Record<ElementId, ElementMeta>,
  hitId: ElementId,
  selectedIds: readonly ElementId[],
  kind: ClickKind,
): ElementId | undefined {
  const chain = chainFor(elements, hitId);
  const top = chain[0];
  if (top === undefined) return undefined;
  const deepest = chain.length - 1;
  if (kind === 'deep') return chain[deepest];

  // Depth of the first selected element that lives in the same top-level tree as the hit.
  let selectedDepth = -1;
  for (const id of selectedIds) {
    const selectedChain = chainFor(elements, id);
    if (selectedChain[0] === top) {
      selectedDepth = selectedChain.length - 1;
      break;
    }
  }
  if (selectedDepth === -1) return top;
  const depth = kind === 'drill' ? selectedDepth + 1 : selectedDepth;
  return chain[Math.min(depth, deepest)];
}

/** Combines a clicked element with the existing selection for Shift + click. */
export function toggleInSelection(selectedIds: readonly ElementId[], id: ElementId): ElementId[] {
  return selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id];
}
